import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Button, Table, Form, Modal, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { getPlayerById } from '../../api/tournament/playerService';
import { getPlayerStatsByPlayer, getPlayerStatById } from '../../api/tournament/playerStatsService';
import '../../Common/ManageTournamentsFixturesTable.css';
import TournamentHeader from '../../Common/TournamentHeader';
import { DeleteModal } from '../../Common/DeleteModal';
import { getTournamentById, ManageTournament, getTournamentSeasons, TournamentSeason } from '../../api/tournament/manageTournamentService';
import { getImageUrl } from '../../utils/imageUrl';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import { PLAYER_PUBLIC_ID_VISIBLE } from '../../constants/featureFlags';
import { hasTournamentWriteAccess } from '../../utils/roleBasedAccess';
import { getSportCapabilities, SportStatField } from '../../api/tournament/capabilitiesService';
import DynamicPlayerStatPresentation from '../../components/tournament/DynamicPlayerStatPresentation';
import {
  buildPlayerStatCreatePayload,
  buildPlayerStatEditPayload,
} from '../../api/tournament/playerStatPayloadAdapter';

const PlayerStatsDetailPage: React.FC = () => {
  const { id: legacyTournamentId, tournamentId: routeTournamentId, playerId, seasonId } = useParams<{
    id?: string;
    tournamentId?: string;
    playerId: string;
    seasonId?: string;
  }>();
  const tournamentId = legacyTournamentId || routeTournamentId;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const canManageStats = hasTournamentWriteAccess(
    { ...user, subRole: role, permissions },
    ['tournament.players.manage', 'tournament.fixtures.manage']
  );
  // Get tournamentSeasonId from route params (seasonId) or location state
  // Use state so it can be updated when player data loads
  const [tournamentSeasonId, setTournamentSeasonId] = useState<string | undefined>(
    seasonId || (location.state as any)?.tournamentSeasonId
  );
  

  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [player, setPlayer] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [season, setSeason] = useState<TournamentSeason | null>(null);
  // Initialize summary based on sport - default to Football, will be updated when sport is determined
  const [summary, setSummary] = useState<any>({ goals: 0, assists: 0, passingAccuracy: 0, shotsOnTarget: 0, tackles: 0 });
  // Store initial playerStats summary to preserve it when matches don't have goal data
  const playerStatsSummaryRef = useRef<any>(null);
  const [search, setSearch] = useState('');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [originalFormData, setOriginalFormData] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rowPendingDelete, setRowPendingDelete] = useState<any | null>(null);
  const [isLoadingMatchDetails, setIsLoadingMatchDetails] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [dynamicStatFields, setDynamicStatFields] = useState<SportStatField[]>([]);

  useEffect(() => {
    if (!tournament?.sports) return;
    getSportCapabilities(tournament.sports)
      .then((capabilities) => setDynamicStatFields(capabilities.fields))
      .catch(() => setDynamicStatFields([]));
  }, [tournament?.sports]);

  // Helper function to get current sport
  const getCurrentSport = (): string => {
    try {
      return getSportValueUtil(tournament, location);
    } catch (sportError) {
      return getSportValueUtil(null, location);
    }
  };

  // Helper function to check if sport uses racket sport stats (Tennis, Badminton, Table Tennis)
  const usesRacketSportStats = (): boolean => {
    const sport = getCurrentSport();
    const sportLower = sport?.toLowerCase() || '';
    return sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
  };

  const recomputeValidity = (data: any) => {
    const matchOk = data && data.matchNum !== undefined && data.matchNum !== null && String(data.matchNum).toString().trim() !== '';
    setIsFormValid(!!matchOk);
  };

  // Check if form has been modified
  const checkFormModified = useCallback(() => {
    if (!originalFormData || !editingRow) {
      // If no original data or not editing, consider as not modified (or modified for new entries)
      setIsFormModified(false);
      return;
    }
    
    // Compare current formData with originalFormData
    // Include racket sport fields if sport is Tennis, Badminton, or Table Tennis
    const isRacketSport = usesRacketSportStats();
    const fieldsToCompare = isRacketSport ? [
      // Tennis fields
      'aces', 'serviceFaults', 'smashes', 'smashWinners', 'drops', 'dropWinners',
      'netShots', 'netKills', 'winners', 'unforcedErrors', 'forcedErrors',
      'setsWon', 'setsLost'
    ] : [
      // Football fields
      'goals', 'assists', 'passesCompleted', 'passesIncompleted', 
      'shots', 'shotsOnTarget', 'tackles', 'interceptions', 'saves',
      'dribbles', 'passesAttempted', 'completeThroughBalls'
    ];
    
    const hasChanges = fieldsToCompare.some(field => {
      const current = formData[field] ?? '';
      const original = originalFormData[field] ?? '';
      // Normalize comparison: convert to number for numeric fields, handle empty strings
      const currentNum = current === '' ? 0 : Number(current);
      const originalNum = original === '' ? 0 : Number(original);
      return currentNum !== originalNum;
    });
    
    setIsFormModified(hasChanges);
  }, [formData, originalFormData, editingRow]);

  // Update form modified state whenever formData changes
  useEffect(() => {
    if (editingRow && !isViewMode) {
      checkFormModified();
    }
  }, [formData, originalFormData, editingRow, isViewMode, checkFormModified]);

  // Calculate passing accuracy percentage
  const calculatePassingAccuracy = (completed: number | string, incompleted: number | string): string => {
    const completedNum = completed === '' || completed === null || completed === undefined ? 0 : Number(completed);
    const incompletedNum = incompleted === '' || incompleted === null || incompleted === undefined ? 0 : Number(incompleted);
    const total = completedNum + incompletedNum;
    if (total === 0 || isNaN(total)) {
      return '0.00';
    }
    const accuracy = (completedNum / total) * 100;
    return accuracy.toFixed(2);
  };

  // Helper function to normalize match data from API response
  const normalizeMatchData = (apiData: any, currentPlayer?: any): any => {
    // Handle different possible response structures
    const data = apiData?.data || apiData;
    
    // playerStats can be an object or array - handle both
    let stat = null;
    if (data?.playerStats) {
      if (Array.isArray(data.playerStats)) {
        stat = data.playerStats[0] || {};
      } else {
        stat = data.playerStats; // It's an object directly
      }
    } else {
      stat = data?.playerStat || data;
    }
    
    const match = data?.match || data;
    // Use currentPlayer parameter (from state) instead of closure variable to avoid stale data
    const playerData = currentPlayer || player;
    
    // Check if sport uses racket sport stats (Tennis, Badminton, Table Tennis)
    const isRacketSport = usesRacketSportStats();
    
    // Base fields common to all sports
    const baseData = {
      playerStatId: stat?._id || data?._id || '',
      matchNum: data?.matchNum || match?.matchNum || stat?.matchNum || '',
      ageCategory: data?.ageCategory?._id || match?.ageCategory?._id || match?.categoryInfo?._id || stat?.ageCategory || '',
      tournament: tournamentId,
      player: (typeof stat?.player === 'object' && stat?.player?._id) ? stat.player._id : (stat?.player || playerData?._id || playerData?.id || ''),
      uniqueId: stat?.uniqueId || ((typeof stat?.player === 'object' && stat?.player?.uniqueId) ? stat.player.uniqueId : playerData?.uniqueId) || '',
      team: stat?.team?._id || match?.team?._id || playerData?.playerSeasonInfo?.team?._id || playerData?.team?._id || playerData?.teamInfo?._id || '',
      teamName: stat?.teamName || stat?.team?.name || match?.team?.name || playerData?.playerSeasonInfo?.team?.name || playerData?.team?.name || playerData?.teamInfo?.name || '',
    };
    
    if (isRacketSport) {
      // Racket sport fields (Tennis, Badminton, Table Tennis)
      // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
      return {
        ...baseData,
        // Service Stats
        aces: (stat?.totalAces != null && stat?.totalAces !== undefined) || (stat?.aces != null && stat?.aces !== undefined) ? Number(stat.totalAces ?? stat.aces ?? 0) : '',
        serviceFaults: (stat?.totalServiceFaults != null && stat?.totalServiceFaults !== undefined) || (stat?.serviceFaults != null && stat?.serviceFaults !== undefined) ? Number(stat.totalServiceFaults ?? stat.serviceFaults ?? 0) : '',
        // Shot Stats
        smashes: (stat?.totalSmashes != null && stat?.totalSmashes !== undefined) || (stat?.smashes != null && stat?.smashes !== undefined) ? Number(stat.totalSmashes ?? stat.smashes ?? 0) : '',
        smashWinners: (stat?.totalSmashWinners != null && stat?.totalSmashWinners !== undefined) || (stat?.smashWinners != null && stat?.smashWinners !== undefined) ? Number(stat.totalSmashWinners ?? stat.smashWinners ?? 0) : '',
        drops: (stat?.totalDrops != null && stat?.totalDrops !== undefined) || (stat?.drops != null && stat?.drops !== undefined) ? Number(stat.totalDrops ?? stat.drops ?? 0) : '',
        dropWinners: (stat?.totalDropWinners != null && stat?.totalDropWinners !== undefined) || (stat?.dropWinners != null && stat?.dropWinners !== undefined) ? Number(stat.totalDropWinners ?? stat.dropWinners ?? 0) : '',
        netShots: (stat?.totalNetShots != null && stat?.totalNetShots !== undefined) || (stat?.netShots != null && stat?.netShots !== undefined) ? Number(stat.totalNetShots ?? stat.netShots ?? 0) : '',
        netKills: (stat?.totalNetKills != null && stat?.totalNetKills !== undefined) || (stat?.netKills != null && stat?.netKills !== undefined) ? Number(stat.totalNetKills ?? stat.netKills ?? 0) : '',
        // Winners & Errors
        winners: (stat?.totalWinners != null && stat?.totalWinners !== undefined) || (stat?.winners != null && stat?.winners !== undefined) ? Number(stat.totalWinners ?? stat.winners ?? 0) : '',
        unforcedErrors: (stat?.totalUnforcedErrors != null && stat?.totalUnforcedErrors !== undefined) || (stat?.unforcedErrors != null && stat?.unforcedErrors !== undefined) ? Number(stat.totalUnforcedErrors ?? stat.unforcedErrors ?? 0) : '',
        forcedErrors: (stat?.totalForcedErrors != null && stat?.totalForcedErrors !== undefined) || (stat?.forcedErrors != null && stat?.forcedErrors !== undefined) ? Number(stat.totalForcedErrors ?? stat.forcedErrors ?? 0) : '',
        // Sets
        setsWon: (stat?.totalSetsWon != null && stat?.totalSetsWon !== undefined) || (stat?.setsWon != null && stat?.setsWon !== undefined) ? Number(stat.totalSetsWon ?? stat.setsWon ?? 0) : '',
        setsLost: (stat?.totalSetsLost != null && stat?.totalSetsLost !== undefined) || (stat?.setsLost != null && stat?.setsLost !== undefined) ? Number(stat.totalSetsLost ?? stat.setsLost ?? 0) : '',
      };
    } else {
      // Football-specific fields
      return {
        ...baseData,
      goals: stat?.goals != null && stat?.goals !== undefined ? Number(stat.goals) : '',
      assists: stat?.assists != null && stat?.assists !== undefined ? Number(stat.assists) : '',
      passesCompleted: (stat?.passesCompleted != null && stat?.passesCompleted !== undefined) || (stat?.completePasses != null && stat?.completePasses !== undefined) ? Number(stat.passesCompleted ?? stat.completePasses ?? 0) : '',
      passesIncompleted: (stat?.passesIncompleted != null && stat?.passesIncompleted !== undefined) || (stat?.incompletePasses != null && stat?.incompletePasses !== undefined) ? Number(stat.passesIncompleted ?? stat.incompletePasses ?? 0) : '',
      shots: (stat?.shots != null && stat?.shots !== undefined) || (stat?.totalShots != null && stat?.totalShots !== undefined) ? Number(stat.shots ?? stat.totalShots ?? 0) : '',
      shotsOnTarget: stat?.shotsOnTarget != null && stat?.shotsOnTarget !== undefined ? Number(stat.shotsOnTarget) : '',
      tackles: stat?.tackles != null && stat?.tackles !== undefined ? Number(stat.tackles) : '',
      interceptions: stat?.interceptions != null && stat?.interceptions !== undefined ? Number(stat.interceptions) : '',
      saves: stat?.saves != null && stat?.saves !== undefined ? Number(stat.saves) : '',
      totalMatches: stat?.totalMatches != null && stat?.totalMatches !== undefined ? Number(stat.totalMatches) : '',
      dribbles: stat?.dribbles != null && stat?.dribbles !== undefined ? Number(stat.dribbles) : '',
      passesAttempted: stat?.passesAttempted != null && stat?.passesAttempted !== undefined ? Number(stat.passesAttempted) : (((stat?.passesCompleted != null && stat?.passesCompleted !== undefined) || (stat?.passesIncompleted != null && stat?.passesIncompleted !== undefined)) ? Number((stat.passesCompleted ?? 0) + (stat.passesIncompleted ?? 0)) : ''),
      completeThroughBalls: stat?.completeThroughBalls != null && stat?.completeThroughBalls !== undefined ? Number(stat.completeThroughBalls) : ''
    };
    }
  };

  // Function to fetch match details from API
  const fetchMatchDetails = async (statId: string, viewMode: boolean = false, matchRowData?: any) => {
    if (!statId) {
      setFormError('Player stat ID is required');
      // Still open modal to show error
      setIsModalOpen(true);
      setIsViewMode(viewMode);
      return;
    }

    setIsLoadingMatchDetails(true);
    setFormError(null);
    
    try {
      // Get sport using utility function
      let sport: string;
      try {
        sport = getSportValueUtil(tournament, location);
      } catch (sportError) {
        // If tournament is not loaded yet, try to get sport from location/URL only
        try {
          sport = getSportValueUtil(null, location);
        } catch {
          // Final fallback
          sport = getCurrentSport();
        }
      }
      
      const response = await getPlayerStatById(statId, sport);
      // Pass current player state to normalizeMatchData to avoid stale closure issues
      const normalizedData = normalizeMatchData(response, player);
      
      setFormData(normalizedData);
      // Store original form data for comparison
      setOriginalFormData(JSON.parse(JSON.stringify(normalizedData)));
      setIsFormModified(false);
      setIsViewMode(viewMode);
      setIsModalOpen(true);
      
      if (!viewMode) {
        setTimeout(() => recomputeValidity(normalizedData), 0);
      }
    } catch (err: any) {
      const resData = err?.response?.data || {};
      const apiMsg = resData.message || resData.error || resData.title || err?.message || 'Failed to load match details';
      setFormError(apiMsg);
      // Open modal even on error so user can see the error message
      setIsModalOpen(true);
      setIsViewMode(viewMode);
      // If we have match row data, try to populate form with what we have
      if (matchRowData) {
        const initialFormData = {
          playerStatId: matchRowData.playerStatId || statId,
          matchNum: matchRowData.matchNum || '',
          ageCategory: matchRowData.ageCategoryId || '',
          tournament: tournamentId,
          player: player?._id || player?.id || '',
          uniqueId: player?.uniqueId || '',
          team: matchRowData.teamId || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
          teamName: matchRowData.teamName || player?.playerSeasonInfo?.team?.name || player?.team?.name || player?.teamInfo?.name || '',
          goals: matchRowData.goals ?? '',
          assists: matchRowData.assists ?? '',
          passesCompleted: matchRowData.passesCompleted ?? '',
          passesIncompleted: matchRowData.passesIncompleted ?? '',
          shots: matchRowData.totalShots ?? matchRowData.shots ?? '',
          shotsOnTarget: matchRowData.shotsOnTarget ?? '',
          tackles: matchRowData.tackles ?? '',
          interceptions: matchRowData.interceptions ?? '',
          saves: matchRowData.saves ?? '',
          totalMatches: '',
          dribbles: '',
          passesAttempted: '',
          completeThroughBalls: ''
        };
        setFormData(initialFormData);
        // Store original form data for comparison
        setOriginalFormData(JSON.parse(JSON.stringify(initialFormData)));
        setIsFormModified(false);
      }
    } finally {
      setIsLoadingMatchDetails(false);
    }
  };

  // Initial load: Get player and tournament data (only once)
  useEffect(() => {
    const loadInitial = async () => {
      if (!tournamentId || !playerId) return;
      setLoading(true);
      setError(null);
      try {
        let sport: string = 'Football';
        try {
          sport = getSportValueUtil(null, location);
        } catch (sportError) {
          const commonSports = ['Football', 'Badminton', 'Tennis', 'Table Tennis'];
          let tempTournament: ManageTournament | null = null;
          let foundSport = false;
          
          for (const trySport of commonSports) {
            try {
              const tempTournamentRes = await getTournamentById(tournamentId, trySport);
              tempTournament = tempTournamentRes || null;
              
              if (tempTournament) {
                sport = tempTournament.sports || trySport;
                foundSport = true;
                setTournament(tempTournament);
                break;
              }
            } catch (err: any) {
              if (err?.response?.status === 404 || err?.message?.includes('not found')) {
                continue;
              }
            }
          }
        }
        
        const [pRes, tRes] = await Promise.all([
          getPlayerById(playerId, tournamentSeasonId, sport),
          getTournamentById(tournamentId, sport)
        ]);
        
        const p = (pRes?.data && (pRes.data._id || pRes.data.name)) ? pRes.data : pRes;
        const tournamentData = tRes || null;
        
        // Update tournament state if not already set
        if (!tournament) {
          setTournament(tournamentData);
        }
        
        const fromTournamentDetailPage = (location.state as any)?.fromTournamentDetailPage || false;
        
        if (tournamentSeasonId && !fromTournamentDetailPage && tournamentData) {
          try {
            const seasons = await getTournamentSeasons(tournamentId, sport);
            const seasonData = seasons.find((s: TournamentSeason) => s._id === tournamentSeasonId);
            if (seasonData) {
              setSeason(seasonData);
            }
          } catch (seasonError: any) {
            // Season details not available for breadcrumb
          }
        }
        
        const flattenedPlayer = p?.playerInfo ? {
          ...p,
          name: p.playerInfo.name || p.name || '',
          profilePic: p.playerInfo.profilePic || p.profilePic || '',
          uniqueId: p.playerInfo.uniqueId || p.uniqueId || p._id
        } : p;
        setPlayer(flattenedPlayer);
        
        if (!tournamentSeasonId && p?.playerSeasonInfo?.tournamentSeason) {
          setTournamentSeasonId(p.playerSeasonInfo.tournamentSeason);
        } else if (!tournamentSeasonId && (location.state as any)?.tournamentSeasonId) {
          setTournamentSeasonId((location.state as any).tournamentSeasonId);
        }
        
        const playerStats = p?.playerSeasonInfo?.playerStats || p?.playerStats;
        if (p && playerStats) {
          const ps = playerStats;
          const sportLower = sport?.toLowerCase() || '';
          const isRacketSport = sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
          
          const playerStatsSummary = isRacketSport ? {
            // Racket sport stats (Tennis, Badminton, Table Tennis)
            // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
            aces: Number(ps.totalAces ?? ps.aces ?? 0),
            serviceFaults: Number(ps.totalServiceFaults ?? ps.serviceFaults ?? 0),
            smashes: Number(ps.totalSmashes ?? ps.smashes ?? 0),
            smashWinners: Number(ps.totalSmashWinners ?? ps.smashWinners ?? 0),
            drops: Number(ps.totalDrops ?? ps.drops ?? 0),
            dropWinners: Number(ps.totalDropWinners ?? ps.dropWinners ?? 0),
            netShots: Number(ps.totalNetShots ?? ps.netShots ?? 0),
            netKills: Number(ps.totalNetKills ?? ps.netKills ?? 0),
            winners: Number(ps.totalWinners ?? ps.winners ?? 0),
            unforcedErrors: Number(ps.totalUnforcedErrors ?? ps.unforcedErrors ?? 0),
            forcedErrors: Number(ps.totalForcedErrors ?? ps.forcedErrors ?? 0),
            setsWon: Number(ps.totalSetsWon ?? ps.setsWon ?? 0),
            setsLost: Number(ps.totalSetsLost ?? ps.setsLost ?? 0)
          } : {
            // Football stats
            goals: Number(ps.goals ?? ps.shots ?? 0),
            assists: Number(ps.assists ?? 0),
            passingAccuracy: Number(ps.passingAccuracy ?? 0),
            shotsOnTarget: Number(ps.shotsOnTarget ?? 0),
            tackles: Number(ps.tackles ?? 0)
          };
          // Store in ref to preserve it when matches are processed
          playerStatsSummaryRef.current = playerStatsSummary;
          setSummary(playerStatsSummary);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load player data');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, [tournamentId, playerId, tournamentSeasonId]);

  const refreshPlayerSummary = async (playerIdToRefresh: string, tournamentSeasonIdToUse?: string) => {
    try {
      // Get sport from tournament state or location state using shared utility
      let sport: string;
      try {
        sport = getSportValueUtil(tournament, location);
      } catch (sportError) {
        // If tournament is not loaded yet, try to get sport from location/URL only
        try {
          sport = getSportValueUtil(null, location);
        } catch {
          // Final fallback
          sport = getCurrentSport();
        }
      }
      const pRes = await getPlayerById(playerIdToRefresh, tournamentSeasonIdToUse, sport);
      const p = pRes?.data || pRes;
      const playerStats = p?.playerSeasonInfo?.playerStats || p?.playerStats;
      
      if (p && playerStats) {
        const ps = playerStats;
        const sportLower = sport?.toLowerCase() || '';
        const isRacketSport = sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
        
        const playerStatsSummary = isRacketSport ? {
          // Racket sport stats (Tennis, Badminton, Table Tennis)
          // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
          aces: Number(ps.totalAces ?? ps.aces ?? 0),
          serviceFaults: Number(ps.totalServiceFaults ?? ps.serviceFaults ?? 0),
          smashes: Number(ps.totalSmashes ?? ps.smashes ?? 0),
          smashWinners: Number(ps.totalSmashWinners ?? ps.smashWinners ?? 0),
          drops: Number(ps.totalDrops ?? ps.drops ?? 0),
          dropWinners: Number(ps.totalDropWinners ?? ps.dropWinners ?? 0),
          netShots: Number(ps.totalNetShots ?? ps.netShots ?? 0),
          netKills: Number(ps.totalNetKills ?? ps.netKills ?? 0),
          winners: Number(ps.totalWinners ?? ps.winners ?? 0),
          unforcedErrors: Number(ps.totalUnforcedErrors ?? ps.unforcedErrors ?? 0),
          forcedErrors: Number(ps.totalForcedErrors ?? ps.forcedErrors ?? 0),
          setsWon: Number(ps.totalSetsWon ?? ps.setsWon ?? 0),
          setsLost: Number(ps.totalSetsLost ?? ps.setsLost ?? 0)
        } : {
          // Football stats
          goals: Number(ps.goals ?? ps.shots ?? 0),
          assists: Number(ps.assists ?? 0),
          passingAccuracy: Number(ps.passingAccuracy ?? 0),
          shotsOnTarget: Number(ps.shotsOnTarget ?? 0),
          tackles: Number(ps.tackles ?? 0)
        };
        
        playerStatsSummaryRef.current = playerStatsSummary;
        setSummary(playerStatsSummary);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404 && err?.response?.data?.statusCode !== 404) {
        console.error('Failed to refresh player summary stats:', err);
      }
    }
  };

  // Load matches with pagination (separate from initial load)
  useEffect(() => {
    const loadMatches = async () => {
      if (!tournamentId || !playerId || !player) return;
      
        const teamId = player?.playerSeasonInfo?.team?._id || 
                    (typeof player?.team === 'object' ? player?.team?._id : null) || 
                    player?.teamInfo?._id || 
                    (typeof player?.team === 'string' ? player?.team : '') || 
                    '';
        if (!teamId) {
        console.error('PlayerStatsDetailPage - loadMatches: teamId is missing', {
          player,
          playerSeasonInfo: player?.playerSeasonInfo,
          team: player?.team,
          teamInfo: player?.teamInfo,
          teamType: typeof player?.team
        });
        return;
      }
      
      const actualPlayerId = player?._id;
      if (!actualPlayerId) {
        console.error('PlayerStatsDetailPage - loadMatches: playerId is missing', { player });
        return;
      }
      
      const ageCategory = player?.playerSeasonInfo?.ageCategory || player?.ageCategory || '';
      
      let finalTournamentSeasonId = tournamentSeasonId || 
                                    player?.playerSeasonInfo?.tournamentSeason || 
                                    (location.state as any)?.tournamentSeasonId;
      
      if (!tournamentSeasonId && player?.playerSeasonInfo?.tournamentSeason) {
        setTournamentSeasonId(player.playerSeasonInfo.tournamentSeason);
        finalTournamentSeasonId = player.playerSeasonInfo.tournamentSeason;
      }
      
      if (!finalTournamentSeasonId) {
        console.error('PlayerStatsDetailPage - loadMatches: tournamentSeasonId is required but not available', {
          tournamentSeasonId,
          playerSeasonInfo: player?.playerSeasonInfo,
          locationState: location.state,
          player
        });
        setMatchesLoading(false);
        setError('Tournament season is required to load player stats. Please ensure the player is associated with a tournament season.');
        return;
      }
      
      setMatchesLoading(true);
      try {
        // Get sport using utility function
        let sport: string;
        try {
          sport = getSportValueUtil(tournament, location);
        } catch (sportError) {
          // If tournament is not loaded yet, try to get sport from location/URL only
          try {
            sport = getSportValueUtil(null, location);
          } catch {
            // Final fallback
            sport = getCurrentSport();
          }
        }
        
        const sRes = await getPlayerStatsByPlayer(tournamentId, teamId, actualPlayerId, ageCategory, currentPage, pageSize, finalTournamentSeasonId, sport);
        
        let statsData: any = null;
        if (Array.isArray(sRes?.data?.paginatedResults)) {
          statsData = sRes.data.paginatedResults;
        } else if (Array.isArray(sRes?.data?.matches)) {
          statsData = sRes.data.matches;
        } else if (Array.isArray(sRes?.paginatedResults)) {
          statsData = sRes.paginatedResults;
        } else if (Array.isArray(sRes?.matches)) {
          statsData = sRes.matches;
        } else if (Array.isArray(sRes?.data)) {
          statsData = sRes.data;
        } else if (Array.isArray(sRes)) {
          statsData = sRes;
        } else {
          statsData = [];
        }
        
        let normalizedMatches: any[] = Array.isArray(statsData) ? statsData : [];
        
        setTotalPages(sRes?.data?.totalPages || sRes?.totalPages || 1);
        setTotalCount(sRes?.data?.totalCount || sRes?.totalCount || normalizedMatches.length);
        
        const sportLower = sport?.toLowerCase() || '';
        const isRacketSport = sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
        
        if (normalizedMatches.length > 0) {
          normalizedMatches = normalizedMatches.map((m: any) => {
            const baseMatchData = {
              _id: '',
              matchNum: '',
              teamName: '',
              teamId: '',
              playerStatId: '',
              ageCategoryId: '',
              hasPlayerStats: false,
            };
            
            if (m.playerStats && m.playerStats !== null) {
              const playerStatsArr = Array.isArray(m.playerStats) ? m.playerStats : (m.playerStats ? [m.playerStats] : []);
              const stat = playerStatsArr[0] || {};
              const base = {
                _id: m._id || stat._id || '',
                matchNum: m.matchNum || m.matchNumber || stat.matchNum || '-',
                teamName: m.team?.name || stat.teamName || stat.team?.name || '-',
                teamId: m.team?._id || stat.team?._id || stat.team || '',
                playerStatId: stat._id || m._id || '',
                ageCategoryId: m.categoryInfo?._id || stat.ageCategory?._id || stat.ageCategory || '',
                hasPlayerStats: true,
              };
              
              if (isRacketSport) {
                return {
                  ...base,
                  // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
                  aces: Number(stat.totalAces ?? stat.aces ?? 0),
                  serviceFaults: Number(stat.totalServiceFaults ?? stat.serviceFaults ?? 0),
                  smashes: Number(stat.totalSmashes ?? stat.smashes ?? 0),
                  smashWinners: Number(stat.totalSmashWinners ?? stat.smashWinners ?? 0),
                  drops: Number(stat.totalDrops ?? stat.drops ?? 0),
                  dropWinners: Number(stat.totalDropWinners ?? stat.dropWinners ?? 0),
                  netShots: Number(stat.totalNetShots ?? stat.netShots ?? 0),
                  netKills: Number(stat.totalNetKills ?? stat.netKills ?? 0),
                  winners: Number(stat.totalWinners ?? stat.winners ?? 0),
                  unforcedErrors: Number(stat.totalUnforcedErrors ?? stat.unforcedErrors ?? 0),
                  forcedErrors: Number(stat.totalForcedErrors ?? stat.forcedErrors ?? 0),
                  setsWon: Number(stat.totalSetsWon ?? stat.setsWon ?? 0),
                  setsLost: Number(stat.totalSetsLost ?? stat.setsLost ?? 0),
                };
              } else {
                return {
                  ...base,
                goals: Number(stat.goals ?? stat.shots ?? stat.totalShots ?? 0),
                assists: Number(stat.assists ?? 0),
                passesCompleted: Number(stat.passesCompleted ?? stat.completePasses ?? 0),
                passesIncompleted: Number(stat.passesIncompleted ?? stat.incompletePasses ?? 0),
                passingAccuracy: stat.passingAccuracy != null ? Number(stat.passingAccuracy) : null,
                totalShots: Number(stat.shots ?? stat.totalShots ?? stat.goals ?? 0),
                shotsOnTarget: Number(stat.shotsOnTarget ?? 0),
                tackles: Number(stat.tackles ?? 0),
                interceptions: Number(stat.interceptions ?? 0),
                saves: Number(stat.saves ?? 0),
              };
              }
            } else if (m.playerStats === null) {
              const base = {
                _id: m._id || '',
                matchNum: m.matchNum || m.matchNumber || '-',
                teamName: m.team?.name || player?.team?.name || player?.teamInfo?.name || '-',
                teamId: m.team?._id || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
                playerStatId: '',
                ageCategoryId: m.categoryInfo?._id || m.ageCategory?._id || m.ageCategory || '',
                hasPlayerStats: false,
              };
              
              if (isRacketSport) {
                return {
                  ...base,
                  aces: 0,
                  serviceFaults: 0,
                  smashes: 0,
                  smashWinners: 0,
                  drops: 0,
                  dropWinners: 0,
                  netShots: 0,
                  netKills: 0,
                  winners: 0,
                  unforcedErrors: 0,
                  forcedErrors: 0,
                  setsWon: 0,
                  setsLost: 0,
                };
              } else {
                return {
                  ...base,
                goals: 0,
                assists: 0,
                passesCompleted: 0,
                passesIncompleted: 0,
                passingAccuracy: null,
                totalShots: 0,
                shotsOnTarget: 0,
                tackles: 0,
                interceptions: 0,
                saves: 0,
              };
              }
            } else {
              const base = {
                _id: m._id || '',
                matchNum: m.matchNum || m.matchNumber || '-',
                teamName: m.team?.name || '-',
                teamId: m.team?._id || m.team || '',
                playerStatId: m._id || '',
                ageCategoryId: m.ageCategory?._id || m.ageCategory || m.categoryInfo?._id || '',
                hasPlayerStats: !!m._id,
              };
              
              if (isRacketSport) {
                return {
                  ...base,
                  aces: Number(m.aces ?? 0),
                  serviceFaults: Number(m.serviceFaults ?? 0),
                  smashes: Number(m.smashes ?? 0),
                  smashWinners: Number(m.smashWinners ?? 0),
                  drops: Number(m.drops ?? 0),
                  dropWinners: Number(m.dropWinners ?? 0),
                  netShots: Number(m.netShots ?? 0),
                  netKills: Number(m.netKills ?? 0),
                  winners: Number(m.winners ?? 0),
                  unforcedErrors: Number(m.unforcedErrors ?? 0),
                  forcedErrors: Number(m.forcedErrors ?? 0),
                  setsWon: Number(m.setsWon ?? 0),
                  setsLost: Number(m.setsLost ?? 0),
                };
              } else {
                return {
                  ...base,
                goals: Number(m.goals ?? m.shots ?? m.totalShots ?? 0),
                assists: Number(m.assists ?? 0),
                passesCompleted: Number(m.passesCompleted ?? m.completePasses ?? 0),
                passesIncompleted: Number(m.passesIncompleted ?? m.incompletePasses ?? 0),
                passingAccuracy: m.passingAccuracy != null ? Number(m.passingAccuracy) : null,
                totalShots: Number(m.shots ?? m.totalShots ?? m.goals ?? 0),
                shotsOnTarget: Number(m.shotsOnTarget ?? 0),
                tackles: Number(m.tackles ?? 0),
                interceptions: Number(m.interceptions ?? 0),
                saves: Number(m.saves ?? 0),
              };
              }
            }
          });
        }
        setMatches(normalizedMatches);
        if (normalizedMatches.length > 0 || sRes?.statusCode === 200) {
          setError(null);
        }
      } catch (e: any) {
        console.error('Failed to load player matches:', e);
        if (e?.response?.status !== 404 && e?.response?.data?.statusCode !== 404) {
          setError(e?.message || e?.response?.data?.message || 'Failed to load player matches');
        } else {
          setMatches([]);
          setError(null);
        }
      } finally {
        setMatchesLoading(false);
      }
    };
    loadMatches();
  }, [tournamentId, playerId, player, currentPage, pageSize, tournamentSeasonId]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Note: Summary stats are always taken from playerStats API response
  // We do NOT recalculate from matches because:
  // 1. Matches are paginated, so they only contain a subset of data
  // 2. The API provides aggregated playerStats which is the source of truth
  // 3. Recalculating from paginated matches would give incorrect totals

  // Small reusable view card for player info + summary
  const PlayerViewCard: React.FC<{ player: any; summary: any }> = ({ player, summary }) => (
    <Card className="mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Card.Header className="d-flex align-items-center" style={{ backgroundColor: '#f8f9fc', borderBottom: '1px solid #e0e0e0' }}>
        <i className="ri-user-line" style={{ fontSize: 18, color: '#6c757d' }}></i>
        <h5 className="mb-0 ms-2" style={{ fontWeight: 600 }}>Player Details</h5>
      </Card.Header>
      <Card.Body>
        {/* Identity row (profile removed as requested) */}
        <div className="row row-cols-5 g-3" style={{ alignItems: 'center', marginBottom: 12 }}>
          {PLAYER_PUBLIC_ID_VISIBLE && (
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Player ID</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>#{player?.uniqueId || player?.playerInfo?.uniqueId || player?.playerSeasonInfo?.uniqueId || player?._id || '-'}</div>
            </div>
          )}
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Player Name</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 18, fontWeight: 600, color: '#212529' }}>{player?.name || '-'}</div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Team Name</Form.Label>
            <div className="d-flex align-items-center gap-2 form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
              {player?.playerSeasonInfo?.team?.logo || player?.team?.logo || player?.teamInfo?.logo ? (
                <img
                  src={getImageUrl(player?.playerSeasonInfo?.team?.logo || player?.team?.logo || player?.teamInfo?.logo || '')}
                  alt="Team"
                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', border: '1px solid #e9ecef' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : null}
              <span>{player?.playerSeasonInfo?.team?.name || player?.team?.name || player?.teamInfo?.name || '-'}</span>
            </div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Age Category</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>{player?.playerSeasonInfo?.ageCategory || player?.ageCategory || '-'}</div>
          </div>
          {/* Spacer column to align with 5-column summary row */}
          <div className="col d-none d-md-block"></div>
        </div>
        {/* Summary row */}
        {dynamicStatFields.length ? (
          <div className="mt-3">
            <DynamicPlayerStatPresentation fields={dynamicStatFields} value={summary} />
          </div>
        ) : usesRacketSportStats() ? (
          <div className="row row-cols-5 g-3" style={{ marginTop: 8 }}>
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Aces</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.aces ?? 0}</div>
            </div>
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Service Faults</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.serviceFaults ?? 0}</div>
            </div>
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Winners</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.winners ?? 0}</div>
            </div>
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Unforced Errors</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.unforcedErrors ?? 0}</div>
            </div>
            <div className="col">
              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Sets Won</Form.Label>
              <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.setsWon ?? 0}</div>
            </div>
          </div>
        ) : (
        <div className="row row-cols-5 g-3" style={{ marginTop: 8 }}>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Goals</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.goals}</div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Assists</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.assists}</div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Passing Accuracy</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.passingAccuracy ? `${Math.round(summary.passingAccuracy)}%` : '0%'}</div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Shots On Target</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.shotsOnTarget}</div>
          </div>
          <div className="col">
            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Tackles</Form.Label>
            <div className="form-control-plaintext" style={{ paddingLeft: 0, marginTop: 2, fontSize: 18, fontWeight: 700, color: '#212529', textAlign: 'left' }}>{summary.tackles}</div>
          </div>
        </div>
        )}
      </Card.Body>
    </Card>
  );

  const fromTournamentDetailPage = (location.state as any)?.fromTournamentDetailPage || false;

  const breadcrumbItems = [
    { 
      label: 'Tournament Registrations',
      onClick: () => navigate('/manage-tournaments-fixtures', {
        state: {
          allTournaments: (location.state as any)?.allTournaments
        }
      })
    },
    { 
      label: tournament?.name || 'Tournament',
      onClick: () => {
        if (tournamentSeasonId && !fromTournamentDetailPage) {
          navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
            state: {
              selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
              allTournaments: (location.state as any)?.allTournaments
            }
          });
        } else {
          navigate(`/manage-tournaments-fixtures/${tournamentId}`, {
            state: {
              selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
              allTournaments: (location.state as any)?.allTournaments
            }
          });
        }
      }
    },
    ...(tournamentSeasonId && !fromTournamentDetailPage ? [
      { 
        label: season?.seasonLabel || 'Season',
        onClick: () => navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, {
          state: {
            activeTab: 'player-stats',
            selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
            allTournaments: (location.state as any)?.allTournaments
          }
        })
      }
    ] : []),
    { label: 'Player Stats', active: true }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Tournament Header for consistency */}
        {/* Header - keep same UI shell, swap content to player image + name */}
        <div style={{ padding: '0 15px', marginBottom: '12px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={() => {
                  const fromTournamentDetailPage = (location.state as any)?.fromTournamentDetailPage;
                  const seasonIdFromState = (location.state as any)?.tournamentSeasonId;
                  const finalSeasonId = tournamentSeasonId || seasonIdFromState;
                  
                  const navigationState: any = {
                    activeTab: 'player-stats',
                    selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
                    allTournaments: (location.state as any)?.allTournaments
                  };
                  
                  if (finalSeasonId && !fromTournamentDetailPage) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${finalSeasonId}`, { 
                      state: navigationState
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: navigationState
                    });
                  }
                }}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="tournament-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
                {(player?.profilePic || player?.playerInfo?.profilePic) ? (
                  <img 
                    src={getImageUrl(player?.profilePic || player?.playerInfo?.profilePic || '')}
                    alt="Player"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = (target.nextElementSibling as HTMLElement);
                      if (fallback) fallback.classList.remove('d-none');
                    }}
                  />
                ) : null}
                {!player?.profilePic && !player?.playerInfo?.profilePic && (
                  <div className="default-tournament-logo-small">
                    <i className="ri-user-line"></i>
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>{player?.name || player?.playerInfo?.name || '-'}</h1>
                {(player?.uniqueId || player?.playerInfo?.uniqueId || player?.playerSeasonInfo?.uniqueId || player?._id) ? (
                  <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                    <span className="tournament-short-name-badge">#{player?.uniqueId || player?.playerInfo?.uniqueId || player?.playerSeasonInfo?.uniqueId || player?._id}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <ShimmerLoader type="tournament-card" />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            {refreshing ? (
              <ShimmerLoader type="tournament-card" />
            ) : (
              <PlayerViewCard player={player} summary={summary} />
            )}

            {/* Matches Header and Filters (single header with Add) */}
            <div className="d-flex justify-content-between align-items-center mb-3" style={{ padding: '0 2px' }}>
              <h4 className="mb-0">Matches</h4>
              <div className="d-flex align-items-center gap-2">
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    placeholder="Search match # or team"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minHeight: '40px', borderRadius: '8px', paddingRight: '32px', minWidth: '260px' }}
                  />
                  {search && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => setSearch('')}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: '#6c757d', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {/* Add button hidden - users can add stats by editing matches with null playerStats */}
              </div>
            </div>
            {/* Matches Listing - table styling */}
            <div className="manage-tournaments-table-container">
              {matchesLoading ? (
                <ShimmerLoader type="table" rows={5} />
              ) : (
              <Table className="mb-0 manage-tournaments-table player-stats-table">
                <thead>
                  <tr>
                    <th>Matches</th>
                    {dynamicStatFields.length ? (
                      <>
                        {dynamicStatFields.map((field) => <th key={field.key}>{field.label}</th>)}
                      </>
                    ) : usesRacketSportStats() ? (
                      <>
                        <th>Aces</th>
                        <th>Service Faults</th>
                        <th>Smashes</th>
                        <th>Smash Winners</th>
                        <th>Drops</th>
                        <th>Drop Winners</th>
                        <th>Net Shots</th>
                        <th>Net Kills</th>
                        <th>Winners</th>
                        <th>Unforced Errors</th>
                        <th>Forced Errors</th>
                        <th>Sets Won</th>
                        <th>Sets Lost</th>
                      </>
                    ) : (
                      <>
                    <th>Assists</th>
                    <th>Complete Passes</th>
                    <th>Incomplete Passes</th>
                    <th>Passing Accuracy (%)</th>
                    <th>Total Shots</th>
                    <th>Shots On Target</th>
                    <th>Tackles</th>
                    <th>Interceptions</th>
                    <th>Saves</th>
                      </>
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = (matches || []).filter((m: any) => {
                      const term = search.trim().toLowerCase();
                      if (!term) return true;
                      const matchStr = String(m.matchNum || '').toLowerCase();
                      const teamStr = String(m.teamName || '').toLowerCase();
                      return matchStr.includes(term) || teamStr.includes(term);
                    });
                    const colSpan = dynamicStatFields.length
                      ? dynamicStatFields.length + 2
                      : usesRacketSportStats() ? 15 : 11;
                    return (!filtered || filtered.length === 0) ? (
                    <tr>
                      <td colSpan={colSpan}><div className="text-center py-5"><div className="empty-state"><i className="ri-ball-pen-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i><div className="empty-state-title">No Matches Found</div></div></div></td>
                    </tr>
                  ) : (
                    filtered.map((m: any) => (
                      <tr key={m._id || m.matchNum}>
                        <td>{m.matchNum || '-'}</td>
                        {dynamicStatFields.length ? (
                          <>
                            {dynamicStatFields.map((field) => <td key={field.key}>{m[field.key] ?? '-'}</td>)}
                          </>
                        ) : usesRacketSportStats() ? (
                          <>
                            <td>{m.aces ?? 0}</td>
                            <td>{m.serviceFaults ?? 0}</td>
                            <td>{m.smashes ?? 0}</td>
                            <td>{m.smashWinners ?? 0}</td>
                            <td>{m.drops ?? 0}</td>
                            <td>{m.dropWinners ?? 0}</td>
                            <td>{m.netShots ?? 0}</td>
                            <td>{m.netKills ?? 0}</td>
                            <td>{m.winners ?? 0}</td>
                            <td>{m.unforcedErrors ?? 0}</td>
                            <td>{m.forcedErrors ?? 0}</td>
                            <td>{m.setsWon ?? 0}</td>
                            <td>{m.setsLost ?? 0}</td>
                          </>
                        ) : (
                          <>
                        <td>{m.assists ?? 0}</td>
                        <td>{m.passesCompleted ?? m.completePasses ?? 0}</td>
                        <td>{m.passesIncompleted ?? m.incompletePasses ?? 0}</td>
                        <td>{(() => {
                          const completed = Number(m.passesCompleted ?? 0);
                          const incompleted = Number(m.passesIncompleted ?? 0);
                          const total = completed + incompleted;
                          if (total === 0) return '0.00';
                          return ((completed / total) * 100).toFixed(2);
                        })()}</td>
                        <td>{m.totalShots ?? m.shots ?? 0}</td>
                        <td>{m.shotsOnTarget ?? 0}</td>
                        <td>{m.tackles ?? 0}</td>
                        <td>{m.interceptions ?? 0}</td>
                        <td>{m.saves ?? 0}</td>
                          </>
                        )}
                        <td>
                          <div className="tournament-actions">
                            <button 
                              className="action-btn btn-view" 
                              title="View"
                              onClick={() => {
                                if (m.playerStatId) {
                                  setEditingRow(m);
                                  fetchMatchDetails(m.playerStatId, true, m);
                                } else {
                                  setFormError('Player stat ID not available');
                                  setIsModalOpen(true);
                                  setIsViewMode(true);
                                }
                              }}
                              disabled={isLoadingMatchDetails}
                            >
                              <i className="ri-eye-line"></i>
                            </button>
                            <button 
                              className="action-btn btn-edit" 
                              title="Edit" 
                              onClick={() => {
                                setEditingRow(m);
                                if (m.playerStatId && m.hasPlayerStats !== false) {
                                  // PlayerStats exists, fetch full details
                                  fetchMatchDetails(m.playerStatId, false, m);
                                } else {
                                  // PlayerStats is null or doesn't exist - open modal with match data for adding stats
                                  setFormError(null); // Clear any previous errors
                                  const isRacketSport = usesRacketSportStats();
                                  const initialFormData = isRacketSport ? {
                                    playerStatId: '', // Empty to trigger ADD API
                                    matchNum: m.matchNum || '',
                                    ageCategory: m.ageCategoryId || '',
                                    tournament: tournamentId,
                                    player: player?._id || player?.id || '',
                                    uniqueId: player?.uniqueId || '',
                                    team: m.teamId || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
                                    teamName: m.teamName || player?.playerSeasonInfo?.team?.name || player?.team?.name || player?.teamInfo?.name || '',
                                    // Racket sport fields (Tennis, Badminton, Table Tennis)
                                    aces: m.aces ?? '',
                                    serviceFaults: m.serviceFaults ?? '',
                                    smashes: m.smashes ?? '',
                                    smashWinners: m.smashWinners ?? '',
                                    drops: m.drops ?? '',
                                    dropWinners: m.dropWinners ?? '',
                                    netShots: m.netShots ?? '',
                                    netKills: m.netKills ?? '',
                                    winners: m.winners ?? '',
                                    unforcedErrors: m.unforcedErrors ?? '',
                                    forcedErrors: m.forcedErrors ?? '',
                                    setsWon: m.setsWon ?? '',
                                    setsLost: m.setsLost ?? ''
                                  } : {
                                    playerStatId: '', // Empty to trigger ADD API
                                    matchNum: m.matchNum || '',
                                    ageCategory: m.ageCategoryId || '',
                                    tournament: tournamentId,
                                    player: player?._id || player?.id || '',
                                    uniqueId: player?.uniqueId || '',
                                    team: m.teamId || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
                                    teamName: m.teamName || player?.playerSeasonInfo?.team?.name || player?.team?.name || player?.teamInfo?.name || '',
                                    // Football fields
                                    goals: m.goals ?? '',
                                    assists: m.assists ?? '',
                                    passesCompleted: m.passesCompleted ?? '',
                                    passesIncompleted: m.passesIncompleted ?? '',
                                    shots: m.totalShots ?? m.shots ?? '',
                                    shotsOnTarget: m.shotsOnTarget ?? '',
                                    tackles: m.tackles ?? '',
                                    interceptions: m.interceptions ?? '',
                                    saves: m.saves ?? '',
                                    totalMatches: '',
                                    dribbles: '',
                                    passesAttempted: '',
                                    completeThroughBalls: ''
                                  };
                                  setFormData(initialFormData);
                                  // Store original form data for comparison
                                  setOriginalFormData(JSON.parse(JSON.stringify(initialFormData)));
                                  setIsFormModified(false);
                                  setIsViewMode(false);
                                  setIsModalOpen(true);
                                  setTimeout(() => recomputeValidity({ matchNum: m.matchNum || '' }), 0);
                                }
                              }}
                              disabled={isLoadingMatchDetails || !canManageStats}
                            >
                              <i className="ri-pencil-line"></i>
                            </button>
                            <button className="action-btn btn-delete" title="Delete" disabled={!canManageStats} onClick={() => {
                              setRowPendingDelete(m);
                              setDeleteError(null);
                              setShowDeleteModal(true);
                            }}><i className="ri-delete-bin-line"></i></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ); })()}
                </tbody>
              </Table>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-section mt-4" style={{ 
                padding: '16px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Page Info */}
                <div className="page-info mb-3">
                  <span className="text-muted" style={{ 
                    fontSize: '14px', 
                    fontWeight: '500',
                    color: '#6c757d'
                  }}>
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} matches
                  </span>
                </div>

                {/* Pagination Controls */}
                <div className="pagination-controls d-flex align-items-center justify-content-center">
                  <div className="page-numbers d-flex align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="me-2"
                      style={{ minWidth: '80px' }}
                    >
                      Previous
                    </Button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className="me-1"
                          style={{ minWidth: '40px' }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ms-2"
                      style={{ minWidth: '80px' }}
                    >
                      Next
                    </Button>
                  </div>
                  
                  <div className="d-flex align-items-center ms-4">
                    <span className="text-muted me-2" style={{ fontSize: '14px' }}>Rows per page:</span>
                    <Form.Select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      size="sm"
                      style={{ width: '80px' }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Form.Select>
                  </div>
                </div>
              </div>
            )}

            {/* Add/Edit/View Modal */}
            <Modal show={isModalOpen} onHide={() => { setIsModalOpen(false); setIsViewMode(false); setEditingRow(null); setOriginalFormData(null); setIsFormModified(false); }} size="lg" backdrop="static" keyboard={false}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {isLoadingMatchDetails ? 'Loading...' : isViewMode ? 'View Match Stats' : editingRow ? 'Edit Match Stats' : 'Add Match Stats'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {isLoadingMatchDetails ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading match details...</p>
                  </div>
                ) : (
                  <>
                    {formError && (
                      <div className="alert alert-danger mb-3">{formError}</div>
                    )}
                    <Form>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Match No<span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          list="matchNumsList" 
                          type="number" 
                          value={formData.matchNum} 
                          onChange={(e) => { const v = e.target.value === '' ? '' : Number(e.target.value); const next = { ...formData, matchNum: v }; setFormData(next); recomputeValidity(next); }} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode || editingRow !== null}
                          disabled={isViewMode || editingRow !== null}
                        />
                        <datalist id="matchNumsList">
                          {Array.from(new Set((matches||[]).map((x:any)=>x.matchNum))).map((num:any)=> (
                            <option key={num} value={num} />
                          ))}
                        </datalist>
                      </Form.Group>
                    </Col>
                    {/* Hidden age category (still submitted) */}
                    <input type="hidden" value={formData.ageCategory || ''} readOnly />
                    {/* Team name is hidden in UI but still sent in payload */}
                    <input type="hidden" value={formData.teamName || ''} readOnly />
                    
                    {/* Conditional fields based on sport */}
                    {dynamicStatFields.length ? (
                      <>
                        {dynamicStatFields.map((field) => (
                          <Col md={3} key={field.key}>
                            <Form.Group>
                              <Form.Label>{field.label}</Form.Label>
                              <Form.Control
                                type={field.type === 'number' ? 'number' : 'text'}
                                value={formData[field.key] ?? ''}
                                onChange={(event) => setFormData({
                                  ...formData,
                                  [field.key]: field.type === 'number'
                                    ? (event.target.value === '' ? '' : Number(event.target.value))
                                    : event.target.value,
                                })}
                                onWheel={(event) => {
                                  if (field.type === 'number') event.currentTarget.blur();
                                }}
                                readOnly={isViewMode}
                                disabled={isViewMode}
                              />
                            </Form.Group>
                          </Col>
                        ))}
                      </>
                    ) : usesRacketSportStats() ? (
                      <>
                        {/* Service Stats */}
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Aces</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.aces ?? ''} 
                              onChange={(e)=>setFormData({...formData, aces: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Service Faults</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.serviceFaults ?? ''} 
                              onChange={(e)=>setFormData({...formData, serviceFaults: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        {/* Shot Stats */}
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Smashes</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.smashes ?? ''} 
                              onChange={(e)=>setFormData({...formData, smashes: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Smash Winners</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.smashWinners ?? ''} 
                              onChange={(e)=>setFormData({...formData, smashWinners: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Drops</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.drops ?? ''} 
                              onChange={(e)=>setFormData({...formData, drops: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Drop Winners</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.dropWinners ?? ''} 
                              onChange={(e)=>setFormData({...formData, dropWinners: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Net Shots</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.netShots ?? ''} 
                              onChange={(e)=>setFormData({...formData, netShots: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Net Kills</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.netKills ?? ''} 
                              onChange={(e)=>setFormData({...formData, netKills: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        {/* Winners & Errors */}
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Winners</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.winners ?? ''} 
                              onChange={(e)=>setFormData({...formData, winners: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Unforced Errors</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.unforcedErrors ?? ''} 
                              onChange={(e)=>setFormData({...formData, unforcedErrors: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Forced Errors</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.forcedErrors ?? ''} 
                              onChange={(e)=>setFormData({...formData, forcedErrors: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        {/* Sets */}
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Sets Won</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.setsWon ?? ''} 
                              onChange={(e)=>setFormData({...formData, setsWon: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Sets Lost</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={formData.setsLost ?? ''} 
                              onChange={(e)=>setFormData({...formData, setsLost: e.target.value === '' ? '' : Number(e.target.value)})} 
                              onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                              readOnly={isViewMode}
                              disabled={isViewMode}
                            />
                          </Form.Group>
                        </Col>
                      </>
                    ) : (
                      <>
                        {/* Football fields */}
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Goals</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.goals ?? ''} 
                          onChange={(e)=>setFormData({...formData, goals: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Assists</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.assists ?? ''} 
                          onChange={(e)=>setFormData({...formData, assists: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Complete Passes</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.passesCompleted ?? ''} 
                          onChange={(e)=>setFormData({...formData, passesCompleted: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Incomplete Passes</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.passesIncompleted ?? ''} 
                          onChange={(e)=>setFormData({...formData, passesIncompleted: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Passing Accuracy (%)</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={`${calculatePassingAccuracy(formData.passesCompleted ?? '', formData.passesIncompleted ?? '')}%`}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                          title="Auto-calculated: (Complete Passes / Total Passes) × 100"
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Auto-calculated
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Total Shots</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.shots ?? ''} 
                          onChange={(e)=>setFormData({...formData, shots: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Shots on Target</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.shotsOnTarget ?? ''} 
                          onChange={(e)=>setFormData({...formData, shotsOnTarget: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Tackles</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.tackles ?? ''} 
                          onChange={(e)=>setFormData({...formData, tackles: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Interceptions</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.interceptions ?? ''} 
                          onChange={(e)=>setFormData({...formData, interceptions: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Saves</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={formData.saves ?? ''} 
                          onChange={(e)=>setFormData({...formData, saves: e.target.value === '' ? '' : Number(e.target.value)})} 
                          onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} 
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}><Form.Group><Form.Label>Dribbles</Form.Label><Form.Control type="number" value={formData.dribbles ?? ''} onChange={(e)=>setFormData({...formData, dribbles: e.target.value === '' ? '' : Number(e.target.value)})} onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} readOnly={isViewMode} disabled={isViewMode} /></Form.Group></Col>
                    <Col md={4}><Form.Group><Form.Label>Complete Through Balls</Form.Label><Form.Control type="number" value={formData.completeThroughBalls ?? ''} onChange={(e)=>setFormData({...formData, completeThroughBalls: e.target.value === '' ? '' : Number(e.target.value)})} onWheel={(e)=> (e.currentTarget as HTMLInputElement).blur()} readOnly={isViewMode} disabled={isViewMode} /></Form.Group></Col>
                      </>
                    )}
                  </Row>
                </Form>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                {isViewMode ? (
                  <Button variant="secondary" onClick={() => { setIsModalOpen(false); setIsViewMode(false); setEditingRow(null); }}>Close</Button>
                ) : (
                  <>
                    <Button variant="secondary" onClick={() => { setIsModalOpen(false); setIsViewMode(false); setEditingRow(null); }} disabled={isSaving || isLoadingMatchDetails}>Cancel</Button>
                <Button variant="primary" onClick={async () => {
                  try {
                    setIsSaving(true);
                    setFormError(null);
                    const { addPlayerStat, editPlayerStat } = await import('../../api/tournament/playerStatsService');
                    const payload = { ...formData };
                    // Always include tournamentId
                    payload.tournament = String(tournamentId);
                    
                    // If tournamentSeasonId is provided (season context), also include it
                    if (tournamentSeasonId) {
                      payload.tournamentSeason = tournamentSeasonId;
                    }
                    
                    // Ensure required ids
                    payload.player = payload.player || (player?._id || player?.id);
                    payload.uniqueId = payload.uniqueId || player?.uniqueId || 0;
                    payload.team = payload.team || (player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '');
                    // Extract age category ID - check if it's an object with _id, otherwise use the value directly (could be ID string)
                    payload.ageCategory = payload.ageCategory || editingRow?.ageCategoryId || player?.ageCategoryId || player?.playerSeasonInfo?.ageCategory?._id || player?.playerSeasonInfo?.ageCategory || player?.ageCategory?._id || player?.ageCategory || '';
                    if (payload.matchNum === '' || payload.matchNum === undefined || payload.matchNum === null) {
                      throw new Error('Match number is required');
                    }
                    if (!payload.tournament || !payload.player) {
                      throw new Error('Match No, Tournament and Player are required');
                    }
                    
                    // Get sport value for API calls (declare here for use in API calls and later refresh)
                    let sportForApi: string;
                    try {
                      sportForApi = getSportValueUtil(tournament, location);
                    } catch (sportError) {
                      try {
                        sportForApi = getSportValueUtil(null, location);
                      } catch {
                        sportForApi = getCurrentSport();
                      }
                    }
                    
                    // Include sports field in payload body (API requires sport in query params OR sports field in body)
                    payload.sports = sportForApi;
                    
                    const isRacketSport = usesRacketSportStats();
                    
                    if (isRacketSport) {
                      // Racket sport fields (Tennis, Badminton, Table Tennis) - coerce all numeric fields
                      const racketSportNumericKeys = [
                        'aces', 'serviceFaults', 'smashes', 'smashWinners', 'drops', 'dropWinners',
                        'netShots', 'netKills', 'winners', 'unforcedErrors', 'forcedErrors',
                        'setsWon', 'setsLost'
                      ];
                      racketSportNumericKeys.forEach((k) => { 
                        payload[k] = payload[k] === '' || payload[k] === null || payload[k] === undefined ? 0 : Number(payload[k] || 0); 
                      });
                    } else {
                      // Football fields - coerce numeric fields
                    // Map goals to shots for API (Goals field in form maps to shots/goals in payload)
                    // If goals field is provided, use it; otherwise use shots field
                    if (payload.goals !== undefined && payload.goals !== '' && payload.goals !== null) {
                      payload.shots = Number(payload.goals);
                      payload.goals = Number(payload.goals);
                    } else if (payload.shots !== undefined && payload.shots !== '' && payload.shots !== null) {
                      payload.shots = Number(payload.shots);
                      payload.goals = Number(payload.shots);
                    } else {
                      payload.shots = 0;
                      payload.goals = 0;
                    }
                    // Remove passingAccuracy from payload - it's calculated on backend
                    delete payload.passingAccuracy;
                    // Remove fields not needed in API: keyPasses, playerOfTheMatch, chances
                    delete payload.keyPasses;
                    delete payload.playerOfTheMatch;
                    delete payload.chances;
                    
                      const footballNumericKeys = ['assists','passesCompleted','passesIncompleted','shotsOnTarget','tackles','interceptions','saves','totalMatches','dribbles','passesAttempted','completeThroughBalls'];
                      footballNumericKeys.forEach((k)=>{ payload[k] = Number(payload[k] || 0); });
                    }
                    
                    // Check if playerStats exists (not null) - use edit API if playerStatId exists, otherwise use add API
                    const playerStatId = formData.playerStatId || (editingRow && editingRow.playerStatId) || '';
                    if (playerStatId) {
                      await editPlayerStat(
                        playerStatId,
                        dynamicStatFields.length
                          ? buildPlayerStatEditPayload(sportForApi, payload, dynamicStatFields)
                          : payload,
                        sportForApi
                      );
                    } else {
                      const matchId = formData.matchId || editingRow?.matchId || editingRow?._id;
                      if (!matchId) {
                        throw new Error('Match ID is required to create player stats');
                      }
                      const createStatValues = dynamicStatFields.reduce<Record<string, unknown>>(
                        (values, field) => {
                          if (payload[field.key] !== undefined && payload[field.key] !== '') {
                            values[field.key] = payload[field.key];
                          }
                          return values;
                        },
                        {}
                      );
                      await addPlayerStat(
                        buildPlayerStatCreatePayload({
                          matchId: String(matchId),
                          playerId: String(payload.player),
                          ...createStatValues,
                        }),
                        sportForApi
                      );
                    }
                    // Refresh UI - reload match data from API to get latest values
                    setIsModalOpen(false);
                    setIsViewMode(false);
                    setEditingRow(null);
                    setOriginalFormData(null);
                    setIsFormModified(false);
                    
                    // Set refreshing state to show shimmer loaders
                    setRefreshing(true);
                    setMatchesLoading(true);
                    
                    // Get teamId and ageCategory from player data for the refresh call (declare outside try for scope)
                    const teamId = player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '';
                    const actualPlayerId = player?._id;
                    const ageCategory = player?.playerSeasonInfo?.ageCategory || player?.ageCategory || '';
                    // Fallback: If tournamentSeasonId is not available, extract it from playerSeasonInfo
                    const finalTournamentSeasonId = tournamentSeasonId || player?.playerSeasonInfo?.tournamentSeason || undefined;
                    
                    // Reuse sportForApi for refresh calls
                    const sportValue = sportForApi;
                    
                    // Reload matches and player data to get fresh data from server
                    try {
                      if (!teamId) {
                        throw new Error('Team ID not found for refresh');
                      }
                      if (!actualPlayerId) {
                        throw new Error('Player ID not found for refresh');
                      }
                      const sRes = await getPlayerStatsByPlayer(tournamentId!, teamId, actualPlayerId, ageCategory, currentPage, pageSize, finalTournamentSeasonId, sportValue);
                      const statsData =
                        (sRes?.data?.matches) ||
                        (sRes?.data?.paginatedResults) ||
                        (Array.isArray(sRes?.data) ? sRes?.data : null) ||
                        (sRes?.matches) ||
                        (sRes?.paginatedResults) ||
                        [];
                      let normalizedMatches: any[] = Array.isArray(statsData) ? statsData : [];
                      
                      // Set pagination info
                      setTotalPages(sRes?.data?.totalPages || sRes?.totalPages || 1);
                      setTotalCount(sRes?.data?.totalCount || sRes?.totalCount || normalizedMatches.length);
                      
                      // Normalize matches - handle both nested playerStats structure, flat structure, and null playerStats
                      // Use the same normalization logic as loadMatches
                      const sportLowerRefresh = sportValue?.toLowerCase() || '';
                      const isRacketSportRefresh = sportLowerRefresh === 'tennis' || sportLowerRefresh === 'badminton' || sportLowerRefresh === 'table tennis' || sportLowerRefresh === 'tt';
                      if (normalizedMatches.length > 0) {
                        normalizedMatches = normalizedMatches.map((m: any) => {
                          const baseMatchData = {
                            _id: '',
                            matchNum: '',
                            teamName: '',
                            teamId: '',
                            playerStatId: '',
                            ageCategoryId: '',
                            hasPlayerStats: false,
                          };
                          
                          if (m.playerStats && m.playerStats !== null) {
                            const playerStatsArr = Array.isArray(m.playerStats) ? m.playerStats : (m.playerStats ? [m.playerStats] : []);
                            const stat = playerStatsArr[0] || {};
                            const base = {
                              _id: m._id || stat._id || '',
                              matchNum: m.matchNum || m.matchNumber || stat.matchNum || '-',
                              teamName: m.team?.name || stat.teamName || stat.team?.name || '-',
                              teamId: m.team?._id || stat.team?._id || stat.team || '',
                              playerStatId: stat._id || m._id || '',
                              ageCategoryId: m.categoryInfo?._id || stat.ageCategory?._id || stat.ageCategory || '',
                              hasPlayerStats: true,
                            };
                            
                            if (isRacketSportRefresh) {
                              return {
                                ...base,
                                // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
                                aces: Number(stat.totalAces ?? stat.aces ?? 0),
                                serviceFaults: Number(stat.totalServiceFaults ?? stat.serviceFaults ?? 0),
                                smashes: Number(stat.totalSmashes ?? stat.smashes ?? 0),
                                smashWinners: Number(stat.totalSmashWinners ?? stat.smashWinners ?? 0),
                                drops: Number(stat.totalDrops ?? stat.drops ?? 0),
                                dropWinners: Number(stat.totalDropWinners ?? stat.dropWinners ?? 0),
                                netShots: Number(stat.totalNetShots ?? stat.netShots ?? 0),
                                netKills: Number(stat.totalNetKills ?? stat.netKills ?? 0),
                                winners: Number(stat.totalWinners ?? stat.winners ?? 0),
                                unforcedErrors: Number(stat.totalUnforcedErrors ?? stat.unforcedErrors ?? 0),
                                forcedErrors: Number(stat.totalForcedErrors ?? stat.forcedErrors ?? 0),
                                setsWon: Number(stat.totalSetsWon ?? stat.setsWon ?? 0),
                                setsLost: Number(stat.totalSetsLost ?? stat.setsLost ?? 0),
                              };
                            } else {
                              return {
                                ...base,
                              goals: Number(stat.goals ?? stat.shots ?? stat.totalShots ?? 0),
                              assists: Number(stat.assists ?? 0),
                              passesCompleted: Number(stat.passesCompleted ?? stat.completePasses ?? 0),
                              passesIncompleted: Number(stat.passesIncompleted ?? stat.incompletePasses ?? 0),
                              passingAccuracy: stat.passingAccuracy != null ? Number(stat.passingAccuracy) : null,
                              totalShots: Number(stat.shots ?? stat.totalShots ?? stat.goals ?? 0),
                              shotsOnTarget: Number(stat.shotsOnTarget ?? 0),
                              tackles: Number(stat.tackles ?? 0),
                              interceptions: Number(stat.interceptions ?? 0),
                              saves: Number(stat.saves ?? 0),
                            };
                            }
                          } else if (m.playerStats === null) {
                            const base = {
                              _id: m._id || '',
                              matchNum: m.matchNum || m.matchNumber || '-',
                              teamName: m.team?.name || player?.team?.name || player?.teamInfo?.name || '-',
                              teamId: m.team?._id || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
                              playerStatId: '',
                              ageCategoryId: m.categoryInfo?._id || m.ageCategory?._id || m.ageCategory || '',
                              hasPlayerStats: false,
                            };
                            
                            if (isRacketSportRefresh) {
                              return {
                                ...base,
                                aces: 0,
                                serviceFaults: 0,
                                smashes: 0,
                                smashWinners: 0,
                                drops: 0,
                                dropWinners: 0,
                                netShots: 0,
                                netKills: 0,
                                winners: 0,
                                unforcedErrors: 0,
                                forcedErrors: 0,
                                setsWon: 0,
                                setsLost: 0,
                              };
                            } else {
                              return {
                                ...base,
                              goals: 0,
                              assists: 0,
                              passesCompleted: 0,
                              passesIncompleted: 0,
                              passingAccuracy: null,
                              totalShots: 0,
                              shotsOnTarget: 0,
                              tackles: 0,
                              interceptions: 0,
                              saves: 0,
                            };
                            }
                          } else {
                            const base = {
                              _id: m._id || '',
                              matchNum: m.matchNum || m.matchNumber || '-',
                              teamName: m.team?.name || '-',
                              teamId: m.team?._id || m.team || '',
                              playerStatId: m._id || '',
                              ageCategoryId: m.ageCategory?._id || m.ageCategory || m.categoryInfo?._id || '',
                              hasPlayerStats: !!m._id,
                            };
                            
                            if (isRacketSportRefresh) {
                              return {
                                ...base,
                                aces: Number(m.aces ?? 0),
                                serviceFaults: Number(m.serviceFaults ?? 0),
                                smashes: Number(m.smashes ?? 0),
                                smashWinners: Number(m.smashWinners ?? 0),
                                drops: Number(m.drops ?? 0),
                                dropWinners: Number(m.dropWinners ?? 0),
                                netShots: Number(m.netShots ?? 0),
                                netKills: Number(m.netKills ?? 0),
                                winners: Number(m.winners ?? 0),
                                unforcedErrors: Number(m.unforcedErrors ?? 0),
                                forcedErrors: Number(m.forcedErrors ?? 0),
                                setsWon: Number(m.setsWon ?? 0),
                                setsLost: Number(m.setsLost ?? 0),
                              };
                            } else {
                              return {
                                ...base,
                              goals: Number(m.goals ?? m.shots ?? m.totalShots ?? 0),
                              assists: Number(m.assists ?? 0),
                              passesCompleted: Number(m.passesCompleted ?? m.completePasses ?? 0),
                              passesIncompleted: Number(m.passesIncompleted ?? m.incompletePasses ?? 0),
                              passingAccuracy: m.passingAccuracy != null ? Number(m.passingAccuracy) : null,
                              totalShots: Number(m.shots ?? m.totalShots ?? m.goals ?? 0),
                              shotsOnTarget: Number(m.shotsOnTarget ?? 0),
                              tackles: Number(m.tackles ?? 0),
                              interceptions: Number(m.interceptions ?? 0),
                              saves: Number(m.saves ?? 0),
                            };
                            }
                          }
                        });
                      }
                      setMatches(normalizedMatches);
                    } catch (refreshErr) {
                      console.error('Failed to refresh matches after save:', refreshErr);
                    }
                    
                    if (playerId) {
                      try {
                        await refreshPlayerSummary(playerId, finalTournamentSeasonId);
                      } catch (summaryErr) {
                        console.error('Failed to refresh player summary after save:', summaryErr);
                      }
                    }
                    setRefreshing(false);
                    setMatchesLoading(false);
                  } catch (err: any) {
                    const resData = err?.response?.data || {};
                    const apiMsg = resData.message || resData.error || resData.title || err?.message || 'Failed to save';
                    setFormError(apiMsg);
                    setRefreshing(false);
                    setMatchesLoading(false);
                  } finally {
                    setIsSaving(false);
                  }
                }} disabled={!canManageStats || isSaving || !isFormValid || isLoadingMatchDetails || (editingRow && !isFormModified)}>{isSaving ? 'Saving...' : (editingRow ? 'Update' : 'Add')}</Button>
                  </>
                )}
              </Modal.Footer>
            </Modal>

            {/* Delete Modal */}
            <DeleteModal
              show={showDeleteModal}
              handleClose={() => { setShowDeleteModal(false); setRowPendingDelete(null); setDeleteError(null); }}
              deleteModalFunction={async () => {
                if (!rowPendingDelete) return;
                try {
                  setIsDeleting(true);
                  setDeleteError(null);
                  const matchId = rowPendingDelete._id || rowPendingDelete.playerStatId;
                  if (!matchId) {
                    throw new Error('Match ID is required for deletion');
                  }
                  
                  // Get sport value for API call (declare here for use in API call and later refresh)
                  let sportForDelete: string;
                  try {
                    sportForDelete = getSportValueUtil(tournament, location);
                  } catch (sportError) {
                    try {
                      sportForDelete = getSportValueUtil(null, location);
                    } catch {
                      sportForDelete = getCurrentSport();
                    }
                  }
                  
                  const { deletePlayerStat } = await import('../../api/tournament/playerStatsService');
                  await deletePlayerStat(matchId, sportForDelete);
                  setShowDeleteModal(false);
                  setRowPendingDelete(null);
                  
                  // Set refreshing state to show shimmer loaders
                  setRefreshing(true);
                  setMatchesLoading(true);
                  
                  // Get teamId and ageCategory from player data for the refresh call (declare outside try for scope)
                  const teamId = player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '';
                  const actualPlayerId = player?._id;
                  const ageCategory = player?.playerSeasonInfo?.ageCategory || player?.ageCategory || '';
                  const finalTournamentSeasonId = tournamentSeasonId || player?.playerSeasonInfo?.tournamentSeason || undefined;
                  
                  // Reuse sportForDelete for refresh calls
                  const sportValue = sportForDelete;
                  
                  // Reload matches and player data to get fresh data from server
                  try {
                    if (!teamId) {
                      throw new Error('Team ID not found for refresh');
                    }
                    if (!actualPlayerId) {
                      throw new Error('Player ID not found for refresh');
                    }
                    
                    const sRes = await getPlayerStatsByPlayer(tournamentId!, teamId, actualPlayerId, ageCategory, currentPage, pageSize, finalTournamentSeasonId, sportValue);
                    
                    // Process matches data
                    const statsData =
                      (sRes?.data?.matches) ||
                      (sRes?.data?.paginatedResults) ||
                      (Array.isArray(sRes?.data) ? sRes?.data : null) ||
                      (sRes?.matches) ||
                      (sRes?.paginatedResults) ||
                      [];
                    let normalizedMatches: any[] = Array.isArray(statsData) ? statsData : [];
                    
                    // Use the same normalization logic as loadMatches
                    const sportLowerDelete = sportValue?.toLowerCase() || '';
                    const isRacketSportDelete = sportLowerDelete === 'tennis' || sportLowerDelete === 'badminton' || sportLowerDelete === 'table tennis' || sportLowerDelete === 'tt';
                    if (normalizedMatches.length > 0) {
                      normalizedMatches = normalizedMatches.map((m: any) => {
                        if (m.playerStats && m.playerStats !== null) {
                          const playerStatsArr = Array.isArray(m.playerStats) ? m.playerStats : (m.playerStats ? [m.playerStats] : []);
                          const stat = playerStatsArr[0] || {};
                          const base = {
                            _id: m._id || stat._id || '',
                            matchNum: m.matchNum || m.matchNumber || stat.matchNum || '-',
                            teamName: m.team?.name || stat.teamName || stat.team?.name || '-',
                            teamId: m.team?._id || stat.team?._id || stat.team || '',
                            playerStatId: stat._id || m._id || '',
                            ageCategoryId: m.categoryInfo?._id || stat.ageCategory?._id || stat.ageCategory || '',
                            hasPlayerStats: true,
                          };
                          
                          if (isRacketSportDelete) {
                            return {
                              ...base,
                              // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
                              aces: Number(stat.totalAces ?? stat.aces ?? 0),
                              serviceFaults: Number(stat.totalServiceFaults ?? stat.serviceFaults ?? 0),
                              smashes: Number(stat.totalSmashes ?? stat.smashes ?? 0),
                              smashWinners: Number(stat.totalSmashWinners ?? stat.smashWinners ?? 0),
                              drops: Number(stat.totalDrops ?? stat.drops ?? 0),
                              dropWinners: Number(stat.totalDropWinners ?? stat.dropWinners ?? 0),
                              netShots: Number(stat.totalNetShots ?? stat.netShots ?? 0),
                              netKills: Number(stat.totalNetKills ?? stat.netKills ?? 0),
                              winners: Number(stat.totalWinners ?? stat.winners ?? 0),
                              unforcedErrors: Number(stat.totalUnforcedErrors ?? stat.unforcedErrors ?? 0),
                              forcedErrors: Number(stat.totalForcedErrors ?? stat.forcedErrors ?? 0),
                              setsWon: Number(stat.totalSetsWon ?? stat.setsWon ?? 0),
                              setsLost: Number(stat.totalSetsLost ?? stat.setsLost ?? 0),
                            };
                          } else {
                            return {
                              ...base,
                            goals: Number(stat.goals ?? stat.shots ?? stat.totalShots ?? 0),
                            assists: Number(stat.assists ?? 0),
                            passesCompleted: Number(stat.passesCompleted ?? stat.completePasses ?? 0),
                            passesIncompleted: Number(stat.passesIncompleted ?? stat.incompletePasses ?? 0),
                            passingAccuracy: stat.passingAccuracy != null ? Number(stat.passingAccuracy) : null,
                            totalShots: Number(stat.shots ?? stat.totalShots ?? stat.goals ?? 0),
                            shotsOnTarget: Number(stat.shotsOnTarget ?? 0),
                            tackles: Number(stat.tackles ?? 0),
                            interceptions: Number(stat.interceptions ?? 0),
                            saves: Number(stat.saves ?? 0),
                          };
                          }
                        } else if (m.playerStats === null) {
                          const base = {
                            _id: m._id || '',
                            matchNum: m.matchNum || m.matchNumber || '-',
                            teamName: m.team?.name || player?.team?.name || player?.teamInfo?.name || '-',
                            teamId: m.team?._id || player?.playerSeasonInfo?.team?._id || player?.team?._id || player?.teamInfo?._id || '',
                            playerStatId: '',
                            ageCategoryId: m.categoryInfo?._id || m.ageCategory?._id || m.ageCategory || '',
                            hasPlayerStats: false,
                          };
                          
                          if (isRacketSportDelete) {
                            return {
                              ...base,
                              aces: 0,
                              serviceFaults: 0,
                              smashes: 0,
                              smashWinners: 0,
                              drops: 0,
                              dropWinners: 0,
                              netShots: 0,
                              netKills: 0,
                              winners: 0,
                              unforcedErrors: 0,
                              forcedErrors: 0,
                              setsWon: 0,
                              setsLost: 0,
                            };
                          } else {
                            return {
                              ...base,
                            goals: 0,
                            assists: 0,
                            passesCompleted: 0,
                            passesIncompleted: 0,
                            passingAccuracy: null,
                            totalShots: 0,
                            shotsOnTarget: 0,
                            tackles: 0,
                            interceptions: 0,
                            saves: 0,
                          };
                          }
                        } else {
                          const base = {
                            _id: m._id || '',
                            matchNum: m.matchNum || m.matchNumber || '-',
                            teamName: m.team?.name || '-',
                            teamId: m.team?._id || m.team || '',
                            playerStatId: m._id || '',
                            ageCategoryId: m.ageCategory?._id || m.ageCategory || m.categoryInfo?._id || '',
                            hasPlayerStats: !!m._id,
                          };
                          
                          if (isRacketSportDelete) {
                            return {
                              ...base,
                              aces: Number(m.aces ?? 0),
                              serviceFaults: Number(m.serviceFaults ?? 0),
                              smashes: Number(m.smashes ?? 0),
                              smashWinners: Number(m.smashWinners ?? 0),
                              drops: Number(m.drops ?? 0),
                              dropWinners: Number(m.dropWinners ?? 0),
                              netShots: Number(m.netShots ?? 0),
                              netKills: Number(m.netKills ?? 0),
                              winners: Number(m.winners ?? 0),
                              unforcedErrors: Number(m.unforcedErrors ?? 0),
                              forcedErrors: Number(m.forcedErrors ?? 0),
                              setsWon: Number(m.setsWon ?? 0),
                              setsLost: Number(m.setsLost ?? 0),
                            };
                          } else {
                            return {
                              ...base,
                            goals: Number(m.goals ?? m.shots ?? m.totalShots ?? 0),
                            assists: Number(m.assists ?? 0),
                            passesCompleted: Number(m.passesCompleted ?? m.completePasses ?? 0),
                            passesIncompleted: Number(m.passesIncompleted ?? m.incompletePasses ?? 0),
                            passingAccuracy: m.passingAccuracy != null ? Number(m.passingAccuracy) : null,
                            totalShots: Number(m.shots ?? m.totalShots ?? m.goals ?? 0),
                            shotsOnTarget: Number(m.shotsOnTarget ?? 0),
                            tackles: Number(m.tackles ?? 0),
                            interceptions: Number(m.interceptions ?? 0),
                            saves: Number(m.saves ?? 0),
                          };
                          }
                        }
                      });
                    }
                    
                    setMatches(normalizedMatches);
                    setTotalPages(sRes?.data?.totalPages || sRes?.totalPages || 1);
                    setTotalCount(sRes?.data?.totalCount || sRes?.totalCount || normalizedMatches.length);
                  } catch (refreshErr) {
                    console.error('Failed to refresh matches after delete:', refreshErr);
                  }
                  
                  if (playerId) {
                    try {
                      await refreshPlayerSummary(playerId, finalTournamentSeasonId);
                    } catch (summaryErr) {
                      console.error('Failed to refresh player summary after delete:', summaryErr);
                    }
                  }
                  setRefreshing(false);
                  setMatchesLoading(false);
                } catch (err: any) {
                  const resData = err?.response?.data || {};
                  const apiMsg = resData.message || resData.error || resData.title || err?.message || 'Failed to delete';
                  setDeleteError(apiMsg);
                  setRefreshing(false);
                  setMatchesLoading(false);
                } finally {
                  setIsDeleting(false);
                }
              }}
              title="Delete Player Match Stat"
              message={rowPendingDelete ? `Are you sure you want to delete Match #${rowPendingDelete.matchNum}? This action cannot be undone.` : 'Are you sure you want to delete this record?'}
              isLoading={isDeleting}
              error={deleteError}
            />
          </>
        )}
      </Container>
    </div>
  );
};

export default PlayerStatsDetailPage;


